<template>
  <div class="w-full h-full space-y-4">

    <Drawer direction="right" :open="showCreateTier" @update:open="(o) => !o && (showCreateTier = false)">
      <DrawerContent class="sm:max-w-md">
        <DrawerHeader class="text-left">
          <DrawerTitle>Add new user segment</DrawerTitle>
          <DrawerDescription>Add your user segments</DrawerDescription>
        </DrawerHeader>
        <div class="flex flex-col gap-3 overflow-y-auto px-4 flex-1">
          <div>
            <label class="text-sm font-medium" for="anct-name">Name</label>
            <Input id="anct-name" v-model="addNewCustomerTier.name" type="text" />
          </div>
          <div>
            <label class="text-sm font-medium" for="anct-description">Description</label>
            <Input id="anct-description" v-model="addNewCustomerTier.description" type="text" />
          </div>
          <div>
            <label class="text-sm font-medium" for="anct-paid-subscription">Paid subscription</label>
            <Select v-model="paidSubscriptionStr">
              <SelectTrigger id="anct-paid-subscription" class="w-full">
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem v-for="o in trueOrFalse" :key="String(o.id)" :value="String(o.id)">{{ o.text }}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DrawerFooter class="flex-row justify-end gap-2">
          <Button variant="outline" @click="showCreateTier = false">Cancel</Button>
          <Button :disabled="!validCustomerTier" @click="addCustomerTier()">Add segment</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>

    <Card>
      <CardHeader>
        <CardTitle>User segments</CardTitle>
        <CardDescription>View your user segments</CardDescription>
        <CardAction>
          <Button id="createContract" size="sm" @click="showCreateTier = true">
            <Plus class="w-4 h-4" />Create user segment
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        <DataTable
          :headers="headersTable1"
          :items="customerTiers"
          :loading="loading"
          :error="error"
          @retry="getCustomerTiers(serverOptions.page - 1, serverOptions.rowsPerPage)"
          v-model:server-options="serverOptions"
          :server-items-length="serverItemsLength"
          searchable
          search-placeholder="Search by segment ID or name"
          :search="searchInput"
          @update:search="searchInput = $event"
          @search="getCustomerTiers(0, serverOptions.rowsPerPage)"
          @clear-search="onClearSearch()"
        >
          <template #item-id="{ id }">
            <CopyId v-if="id" :value="id" :max-width="120" />
            <span v-else class="text-muted-foreground">—</span>
          </template>
          <template #item-name="{ name }">
            <span v-if="name" class="text-sm font-medium truncate" :title="name">{{ name }}</span>
            <span v-else class="text-muted-foreground">—</span>
          </template>
          <template #item-paidSubscription="{ paidSubscription }">
            <Badge v-if="paidSubscription" variant="success">Yes</Badge>
            <Badge v-else variant="secondary">No</Badge>
          </template>
          <!-- Delete lives on the segment page itself (behind a confirm), so it is not
               offered here where a mis-click would destroy a segment from a list. -->
          <template #item-action="audience">
            <div class="flex justify-end items-center gap-1">
              <Button
                size="sm"
                variant="outline"
                @click="$router.push('/enrolled-users/user-segments/' + audience.id)"
              >View segment</Button>
            </div>
          </template>
        </DataTable>
      </CardContent>
    </Card>
  </div>
</template>

<script setup></script>

<script>
import { DateTime } from 'luxon'
import {
  Card, CardHeader, CardTitle, CardDescription, CardContent, CardAction,
  Button,
  Badge,
  CopyId,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter,
  Input,
} from '@boogi/ui'
import { Plus } from 'lucide-vue-next'
import { DataTable } from '@boogi/ui'

export default {
  name: 'manage-publsihers',
  components: {
    DataTable,
    Card, CardHeader, CardTitle, CardDescription, CardContent, CardAction,
    Button,
    Badge,
    CopyId,
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
    Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter,
      Input,
    Plus,
  },
  props: {},

  data() {
    return {
      showCreateTier: false,
      loading: false,
      error: false,
      serverOptions: {
        page: 1,
        rowsPerPage: 50,
      },

      trueOrFalse: [
        { id: true, text: 'Yes' },
        { id: false, text: 'No' },
      ],

      serverItemsLength: undefined,

      addNewCustomerTier: {
        name: '',
        description: '',
        paidSubscription: undefined,
      },

      headersTable1: [
        { text: 'Name', value: 'name' },
        { text: 'Id', value: 'id' },
        { text: 'Paid', value: 'paidSubscription' },
        { text: 'Action', value: 'action' },
      ],
      searchInput: '',
      customerTiers: [],
    }
  },
  created() {
    this.getCustomerTiers(0, 50)
  },
  methods: {
    niceDateTime(dateObject) {
      return DateTime.fromMillis(dateObject).toLocaleString(DateTime.DATE_SHORT)
    },

    async addCustomerTier() {
      try {
        const token = localStorage.getItem('token')
        const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

        this.loading = true

        const response = await this.$apiFetch(`${apiUrl}/private/api/client/customer-tier`, {
          method: 'POST',
          body: JSON.stringify(this.addNewCustomerTier),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        })

        if (response.ok) {
          this.loading = true
          this.showCreateTier = false
          this.addNewCustomerTier = {
            name: '',
            description: '',
            paidSubscription: undefined,
          }
          this.getCustomerTiers(0, 50)
        }
      } catch (error) {
        console.log(error)
        return
      }
    },

    async getCustomerTiers(page, size) {
      const token = localStorage.getItem('token')
      const apiUrl = localStorage.getItem('apiUrl') || process.env.VUE_APP_API_URL

      this.loading = true
      this.error = false
      try {
        const searchParam = this.searchInput ? `&search=${encodeURIComponent(this.searchInput)}` : ''
        const response = await this.$apiFetch(
          `${apiUrl}/private/api/client/customer-tier?page=${page}&size=${size}${searchParam}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `${token}`,
            },
          }
        )

        if (response.ok) {
          var object = await response.json()
          this.serverItemsLength = object.totalElements
          this.customerTiers = object.content
        } else {
          this.error = true
        }
      } catch (e) {
        this.error = true
      } finally {
        this.loading = false
      }
    },

  },
  computed: {
    paidSubscriptionStr: {
      get() {
        return this.addNewCustomerTier.paidSubscription ? 'true' : 'false'
      },
      set(value) {
        this.addNewCustomerTier.paidSubscription = value === 'true'
      },
    },
    validCustomerTier() {
      return (
        this.addNewCustomerTier.paidSubscription != null &&
        this.addNewCustomerTier.name != '' &&
        this.addNewCustomerTier.description != ''
      )
    },
  },
  watch: {
    serverOptions: {
      deep: true,
      handler(val) {
        this.getCustomerTiers(val.page - 1, val.rowsPerPage)
      },
    },
  },
}
</script>
